/**
 * AuthLayout - Split-screen layout for authentication pages
 *
 * Left: ink brand panel with the reversed wordmark and the editorial
 * "Decision Cut" headline (Instrument Serif). Right: form area on canvas.
 * The brand panel collapses to a compact header on small screens.
 */

import * as React from 'react';
import { Link } from 'react-router-dom';

import { Wordmark } from '@/components/brand/Wordmark';
import { ThemeToggle } from '@/components/ui/ThemeToggle';

export interface AuthLayoutProps {
  /** Headline above the form card */
  title: string;
  /** Supporting copy under the title */
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  /** Small print rendered under the card */
  footer?: React.ReactNode;
}

export function AuthLayout({ title, subtitle, children, footer }: AuthLayoutProps): React.ReactElement {
  return (
    <div className="flex min-h-screen bg-canvas">
      {/* Brand panel */}
      <aside className="relative hidden w-[44%] max-w-[640px] flex-col justify-between overflow-hidden bg-gradient-hero p-12 lg:flex">
        <div className="pointer-events-none absolute inset-0 bg-gradient-glow" aria-hidden="true" />

        <Link
          to="/"
          aria-label="Adverdict home"
          className="relative z-10 inline-flex w-fit rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
        >
          <Wordmark tone="white" className="h-[22px]" />
        </Link>

        <div className="relative z-10">
          <h2 className="text-editorial text-[44px] leading-[1.08] text-white">
            Creative intelligence.
            <br />
            <span className="italic text-white/80">Clearer decisions.</span>
          </h2>
          <p className="mt-6 max-w-md text-md leading-relaxed text-white/60">
            Adverdict combines qualitative creative insight with performance
            data — so you can invest in what actually works.
          </p>

          {/* Quiet proof points, product-led not hypey */}
          <div className="mt-10 flex flex-wrap gap-2">
            {['Insight', 'Hook', 'Retention', 'Performance'].map((label) => (
              <span
                key={label}
                className="rounded-pill border border-white/15 px-3 py-1 text-xs font-semibold tracking-wide text-white/70"
              >
                {label}
              </span>
            ))}
          </div>
        </div>

        <p className="relative z-10 text-xs text-white/40">
          © {new Date().getFullYear()} Adverdict. Evidence over opinion.
        </p>
      </aside>

      {/* Form area */}
      <main className="relative flex min-h-screen flex-1 flex-col">
        {/* Compact header on small screens + theme toggle */}
        <div className="flex items-center justify-between px-6 py-5 sm:px-10">
          <Link
            to="/"
            aria-label="Adverdict home"
            className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 lg:invisible"
          >
            <Wordmark tone="auto" className="h-[18px]" />
          </Link>
          <ThemeToggle />
        </div>

        <div className="flex flex-1 items-center justify-center px-4 pb-16 pt-2 sm:px-10">
          <div className="w-full max-w-md">
            <div className="mb-8">
              <h1 className="text-3xl font-bold leading-tight tracking-tight text-primary">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-2 text-md leading-relaxed text-secondary">{subtitle}</p>
              )}
            </div>

            {children}

            {footer && <div className="mt-6 text-center text-xs text-muted">{footer}</div>}
          </div>
        </div>
      </main>
    </div>
  );
}
